import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast
} from '@chakra-ui/react';
import AvatarDefault from '../../../../assets/medium_default_optimized.png';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../assets/founding_creator_icon.png';
import FollowButton from '../../../../components/FollowButton';
import BadgeWithTooltip from '../../../../components/BadgeWithTooltip';
import { BADGE_INFO } from '../../../../constants/badges';
import EyeDarkIcon from '../../../../assets/eye-dark-icon.svg';
import ShareIcon from '../../../../assets/share-gray-icon.svg';
import SavedIcon from '../../../../assets/saved-gray-icon.svg';
import SaveIcon from '../../../../assets/save-gray-icon.svg';
import IgnoreIcon from '../../../../assets/ignore-gray-icon.svg';
import UnignoreIcon from '../../../../assets/ignore-green-icon.svg';
import CommentIcon from '../../../../assets/comment-article-icon.svg';
import ShareContentModal from '../../../../components/ShareContentModal';
import AwardWriterModal from '../../../../components/AwardWriterModal';
import InvestArticleModal from '../../../../components/InvestArticleModal';
import CommentArticleIcon from '../../../../assets/comment-article-icon.svg';
import TwygsLogoIcon from '../../../../assets/twyg-logo-icon.svg';
import FlagIcon from '../../../../assets/flag.svg';
import { useFollowUserMutation } from '../../../../apis/queries/useFollowUserMutation';
import { useBookmarkArticleMutation } from '../../../../apis/queries/useBookmarkArticleMutation';
import { convertCountLike } from '../../../../constants';
import ExpandButton from '../../../../components/ExpandButton';
import { useIgnoreArticleMutation } from '../../../../apis/queries/useIgnoreArticleMutation';
import { MoreHorizontal } from 'lucide-react';
import ReportSidePanel from '../GeneralComment/components/ReportSidePanel';
import { profileAtom } from '../../../../components/Settings/atom';
import { useAtom } from 'jotai';
import { commentApi } from '../../../../apis/comment.api';
import ToastBtnMessage from '../../../../components/ToastBtnMessage';
import ContributorDetail from './contributor-detail.component';
import { useNavigate } from 'react-router-dom';
import ReactStackIcon from '../../../../assets/react-stack.svg';
import PointIcon from '../../../../assets/point-icon.svg';
import PointBoldIcon from '../../../../assets/point-bold-icon.svg';
import CheckedIcon from '../../../../assets/checked-icon.svg';
import PlusGreenIcon from '../../../../assets/plus-bold-icon.svg';
import ArticleReactionStatsModal from './stats-modal.component';
import DataLayer from '../../../../utils/data-layer';
import useTruncatedElement from '../../../../hooks/useTruncatedElement';
import { ResourceType } from '../../../../types/moderation.types';
import { createReputation, investArticle } from 'src/apis/article.api';
import RankBadge from '../../../../components/RankBadge';
import { WriterBadgeInfo } from '../../../../components/WriterBadgeInfo';
import SensitiveContentCard from '../../../../components/SensitiveContentCard';
import useRankBadgeTooltip from 'src/hooks/useRankBadgeTooltip';
import LearnMorePanel from '../LearnMorePanel/index';
import InvestmentButton from './investment-button.component';
import { handleUserClick } from 'src/utils/helper.util';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

const ArticleHeader = ({
  showSensitiveContentCard,
  setShowSensitiveContentCard,
  article: initialArticle,
  refetch,
  isLoading
}) => {
  const [article, setArticle] = useState(initialArticle);

  // Update local state when prop changes
  useEffect(() => {
    setArticle(initialArticle);
  }, [initialArticle]);
  const { mutate: saveArticle, isPending: isSavingArticle } = useBookmarkArticleMutation();
  const { mutate: ignoreArticle, isPending: isIgnoringArticle } = useIgnoreArticleMutation();
  const { mutateAsync: followUser, isPending: isFollowingUser } = useFollowUserMutation(
    article?.id
  );
  const navigate = useNavigate();

  const [showAllContrubutors, setShowAllContrubutors] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);

  const filteredContributors =
    article?.contributors?.filter((contributor) => contributor?.id !== article?.creatorId) || [];

  const hasMoreThanSix = filteredContributors.length > 6;
  const displayedContributors = showAllContrubutors
    ? filteredContributors
    : filteredContributors.slice(0, 6);

  const hasMoreThanNine = article?.topics.length > 9;
  const displayedTopics = showAllTopics ? article?.topics : article?.topics.slice(0, 9);

  const queryClient = useQueryClient();

  const firstDescriptionRef = useRef(null);
  const secondDescriptionRef = useRef(null);
  const thirdDescriptionRef = useRef(null);

  const firstTruncation = useTruncatedElement({
    ref: firstDescriptionRef
  });

  const secondTruncation = useTruncatedElement({
    ref: secondDescriptionRef
  });

  const thirdTruncation = useTruncatedElement({
    ref: thirdDescriptionRef
  });
  const { mutateAsync: ignoreUser } = useMutation({
    mutationFn: commentApi.blockUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ignore']
      });
    }
  });

  const { mutateAsync: createPoint, isPending: isCreatingPoint } = useMutation({
    mutationFn: createReputation
  });

  const { mutateAsync: investPointArticle, isPending: isInvesting } = useMutation({
    mutationFn: investArticle
  });

  const [modal, setModal] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [openLearnMorePanel, setOpenLearnMorePanel] = useState(false);
  const [reportType, setReportType] = useState('');
  const [profile, setProfile] = useAtom(profileAtom);
  const toast = useToast();
  const [userId] = useState(JSON.parse(localStorage.getItem('user'))?.id);
  const {
    openPopup,
    totalAwardedPoints,
    isTouchDevice,
    handleMouseEnter,
    handleClick,
    handleMouseLeave
  } = useRankBadgeTooltip();

  const handleCreatePoint = async (point: number) => {
    try {
      const result: any = await createPoint({
        amount: point,
        recipientId: article?.creatorId,
        entityId: article?.id,
        entityType: 'article'
      });
      toast({
        render: () => (
          <ToastBtnMessage
            title="Successfully awarded the article!"
            isError={false}
            isHiddenButton
          />
        ),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });

      // Track article award event
      if (article && article.id) {
        const articleType = article.writingType?.name;
        DataLayer.trackArticleAward(articleType, article.id);
      }

      setProfile({ ...profile, dailyPointsRemaining: result?.dailyPointsRemaining });
      setModal('');
      refetch();
    } catch (error) {
      toast({
        render: () => (
          <ToastBtnMessage title={error?.response?.data?.message} isError={true} isHiddenButton />
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const handleInvestPoints = async (point: number) => {
    try {
      await investPointArticle({
        articleId: article?.id,
        pointInvested: point
      });
      setProfile({ ...profile, totalRawPointsRemaining: profile.totalRawPointsRemaining - point });
      toast({
        render: () => (
          <ToastBtnMessage
            title="Thanks for investing in this article! Your support helps boost great content!"
            isError={false}
            btnTitle="View Investments"
            btnAction={() => navigate('/article/manage?tab=investments')}
          />
        ),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
      refetch();
    } catch (error) {
      toast({
        render: () => (
          <ToastBtnMessage title={error?.response?.data?.message} isError={true} isHiddenButton />
        ),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setModal('');
    }
  };

  const handleSaveArticle = () => {
    if (!userId) return UnauthorizedDispatcher('required');
    saveArticle({
      articleId: article?.id,
      isSaved: article?.isSaved,
      article
    });
  };

  const handleIgnoreArticle = () => {
    if (!userId) return UnauthorizedDispatcher('required');
    ignoreArticle({
      articleId: article?.id,
      isIgnored: article?.isIgnored,
      toastIgnore: undefined
    });
  };

  const handleIgnoreUser = async () => {
    try {
      if (!userId) return UnauthorizedDispatcher('required');
      // Track article ignore event
      if (article) {
        DataLayer.trackWriterIgnore(article?.creator?.username);
      }
      await ignoreUser(article?.creatorId);

      toast({
        render: () => (
          <ToastBtnMessage title="Successfully ignored the writer" isError={false} isHiddenButton />
        ),
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    } catch (error) {
      toast({
        render: () => (
          <ToastBtnMessage title="Failed to ignore the writer" isError={true} isHiddenButton />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const handleFollowUser = async () => {
    try {
      if (!userId) return UnauthorizedDispatcher('required');
      const wasFollowing = article?.creator.isFollowing;

      await followUser({
        userId: article?.creator.id,
        isFollowing: article?.creator.isFollowing,
        writerUsername: article?.creator.username
      });

      if (article?.investmentData.pointInvested) {
        if (wasFollowing) {
          toast({
            render: () => (
              <ToastBtnMessage
                title="Your investment is frozen because you unfollowed the writer. Re-follow to reactivate your support."
                isError={true}
                isHiddenButton
              />
            ),
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right'
          });
        } else {
          toast({
            render: () => (
              <ToastBtnMessage
                title="Your investment is now active again!"
                isError={false}
                btnTitle="View Investments"
                btnAction={() => navigate('/article/manage?tab=investments')}
              />
            ),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right'
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleScrollToGeneralComments = () => {
    const generalCommentsElement = document.getElementById('general-comment');
    const headerHeight = 92; // Adjust for the header height

    if (generalCommentsElement) {
      const elementPosition = generalCommentsElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // useEffect(() => {
  //   setTimeout(() => {
  //     const hash = window.location.hash;
  //     if (hash === '#general-comment') {
  //       handleScrollToGeneralComments();
  //     }
  //   }, 1000);
  // }, []);

  return (
    <div className="flex flex-col gap-2 md:gap-4 pt-3 md:pt-14 pb-4 md:pb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 md:gap-5 w-full">
          <img
            src={article?.creator.imgUrl || AvatarDefault}
            alt=""
            className="w-10 h-10 md:w-[84px] md:h-[84px] rounded-full object-cover flex-shrink-0 cursor-pointer"
            onClick={(e) =>
              handleUserClick(e, () => {
                navigate(`/${article?.creator.username}`);
              })
            }
          />
          <div className="flex flex-col w-full">
            <div
              className="flex flex-wrap items-center gap-1 md:gap-2 cursor-pointer"
              onClick={(e) =>
                handleUserClick(e, () => {
                  navigate(`/${article?.creator.username}`);
                })
              }
            >
              <span className="font-[600] text-sm md:text-xl hover:text-growth-green-500 transition duration-200">
                {article?.creator.firstName}
              </span>
              {article?.creator.isVerifiedAuthor && (
                <BadgeWithTooltip
                  icon={VerifiedIcon}
                  alt="Verified Author"
                  title={BADGE_INFO.VERIFIED_AUTHOR.title}
                  description={BADGE_INFO.VERIFIED_AUTHOR.description}
                  className="w-6 h-6"
                />
              )}
              {article?.creator?.isFoundingCreator && (
                <BadgeWithTooltip
                  icon={FoundingCreatorIcon}
                  alt="Founding Creator"
                  title={BADGE_INFO.FOUNDING_CREATOR.title}
                  description={BADGE_INFO.FOUNDING_CREATOR.description}
                  className="w-6 h-6"
                />
              )}
              {article?.creator?.username && (
                <span className="font-[600] text-xs md:text-lg text-02-dim-gray">
                  @{article?.creator?.username}
                </span>
              )}
              {article?.creator?.id === profile.id && (
                <div className="px-2 py-1 rounded-[80px] bg-growth-green-50 text-sm text-growth-green-900 font-[600]">
                  You
                </div>
              )}
              <div
                className="relative"
                onMouseEnter={() => !isTouchDevice && handleMouseEnter(article?.creator.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(article?.creator.id);
                }}
                onMouseLeave={() => !isTouchDevice && handleMouseLeave()}
              >
                <RankBadge rank={article?.creator?.currentRank} />
                {openPopup && (
                  <div className="absolute top-[34px] md:top-[-20px] left-[-50%] md:left-[110px] z-10">
                    <WriterBadgeInfo
                      rank={article?.creator?.currentRank}
                      totalReceivedPoint={totalAwardedPoints.totalReceivedPoint}
                      totalReceivedUsers={totalAwardedPoints.totalReceivedUsers}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-[8px] w-full hidden md:!block">
              <p
                ref={firstDescriptionRef}
                className={`font-sm text-sm font-ui p-0 bg-white text-black ${
                  !firstTruncation.isReadingMore && 'line-clamp-[10]'
                } whitespace-pre-wrap`}
              >
                {article?.creator.description || '...'}
              </p>
              {firstTruncation.isTruncated && (
                <div onClick={firstTruncation.toggleIsShowingMore} className="cursor-pointer">
                  <p className="text-sm font-[900] text-growth-green-500 cursor-pointer hover:opacity-80 mt-[8px]">
                    {firstTruncation.isReadingMore ? 'Show less' : 'Read more'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {userId !== article?.creator.id && (
            <FollowButton
              isFollowing={article?.creator.isFollowing}
              onClick={handleFollowUser}
              isLoading={isLoading}
            />
          )}
          <Menu>
            <MenuButton
              as={Button}
              variant="unstyled"
              border="none"
              boxShadow="none"
              h={{ base: '24px', md: '32px' }}
              w={{ base: '24px', md: '32px' }}
              rounded="full"
              _hover={{
                bg: '#F0F0F0'
              }}
            >
              <MoreHorizontal className="ml-1 w-4 h-4 md:w-6 md:h-6" />
            </MenuButton>

            <MenuList minW={'unset'} p={'0.625rem'} shadow={'md'} rounded={'12px'}>
              <MenuItem
                _hover={{ bgColor: 'anti-flash-white' }}
                rounded={'0.5rem'}
                display={article?.creatorId === profile.id ? 'none' : 'flex'}
                py={'8px'}
                px={'10px'}
                fontSize={'sm'}
                fontWeight={900}
                _focus={{ bgColor: 'anti-flash-white' }}
                icon={<img src={FlagIcon} alt="" className="w-5 h-5" />}
                onClick={() => {
                  setOpenReportModal(true);
                  setReportType(ResourceType.USER);
                }}
              >
                Report Writer
              </MenuItem>
              <MenuItem
                _hover={{ bgColor: 'anti-flash-white' }}
                rounded={'0.5rem'}
                display={article?.creatorId === profile.id ? 'none' : 'flex'}
                py={'8px'}
                px={'10px'}
                fontSize={'sm'}
                fontWeight={900}
                _focus={{ bgColor: 'anti-flash-white' }}
                icon={<img src={IgnoreIcon} alt="" className="w-5 h-5" />}
                onClick={handleIgnoreUser}
              >
                Ignore
              </MenuItem>
              <MenuItem
                _hover={{ bgColor: 'anti-flash-white' }}
                rounded={'0.5rem'}
                py={'8px'}
                px={'10px'}
                fontSize={'sm'}
                fontWeight={900}
                _focus={{ bgColor: 'anti-flash-white' }}
                icon={<img src={ShareIcon} alt="" className="w-5 h-5" />}
                onClick={() => setModal('share-writer')}
              >
                Share
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </div>
      <div className="mt-[8px] w-full block md:!hidden">
        <p
          ref={secondDescriptionRef}
          className={`font-sm text-sm 2xl:text-base font-ui p-0 bg-white text-black ${
            !secondTruncation.isReadingMore && 'line-clamp-[10]'
          } whitespace-pre-wrap`}
        >
          {article?.creator.description || '...'}
        </p>
        {secondTruncation.isTruncated && (
          <div onClick={secondTruncation.toggleIsShowingMore} className="cursor-pointer">
            <p className="text-sm font-[900] text-growth-green-500 cursor-pointer hover:opacity-80 mt-[8px]">
              {secondTruncation.isReadingMore ? 'Show less' : 'Read more'}
            </p>
          </div>
        )}
      </div>
      {filteredContributors.length > 0 && (
        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
          <span className="text-sm md:text-base font-[800]">Contributors:</span>
          <div className="flex flex-wrap gap-2">
            {displayedContributors?.map((contributor, index) => (
              <div key={index} className="relative">
                <ContributorDetail articleId={article?.id} user={contributor} index={index} />
              </div>
            ))}
            {hasMoreThanSix && (
              <button
                className="text-growth-green-500 font-[900] text-sm hover:opacity-80"
                onClick={() => setShowAllContrubutors(!showAllContrubutors)}
              >
                {showAllContrubutors ? 'Hide' : 'View more'}
              </button>
            )}
          </div>
        </div>
      )}

      <Divider className="z-[-10]" borderColor="anti-flash-white" />

      <div className="relative">
        <div
          className={`${showSensitiveContentCard && !article?.isShowSensitive ? 'block' : 'hidden'} absolute top-[20%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10`}
        >
          <SensitiveContentCard
            actionButton={article?.moderation?.actionButton}
            primarySource={article?.moderation?.primarySource}
            primaryViolation={article?.moderation?.primaryViolation}
            setShowSensitiveContentCard={setShowSensitiveContentCard}
            articleId={article?.id}
            isShowSensitive={article?.isShowSensitive}
          />
        </div>
        <div
          className={`${showSensitiveContentCard && !article?.isShowSensitive ? 'blur-lg' : ''} flex flex-col gap-2 md:gap-4`}
        >
          <h1 className="text-2xl md:text-[32px] font-[800] mt-4 md:mt-6">{article?.title}</h1>
          <div>
            <h3
              ref={thirdDescriptionRef}
              className={`text-sm md:text-base font-ui whitespace-pre-wrap ${!thirdTruncation.isReadingMore && 'line-clamp-4'}`}
            >
              {article?.description}
            </h3>

            {thirdTruncation.isTruncated && (
              <div onClick={thirdTruncation.toggleIsShowingMore} className="cursor-pointer">
                <p className="text-sm font-[900] text-growth-green-500 cursor-pointer hover:opacity-80 mt-[8px]">
                  {thirdTruncation.isReadingMore ? 'Show less' : 'Read more'}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 md:gap-4">
            {displayedTopics?.map((topic) => (
              <span
                key={topic.id}
                className="px-3 py-1 bg-[#F0EDEC] text-earth-brown-500 rounded-full text-xs md:text-sm font-[600] cursor-pointer hover:bg-[#E8E8E8] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/recommended-topics?topicId=${topic.id}`);
                }}
              >
                {topic.name}
              </span>
            ))}
            {hasMoreThanNine && (
              <button
                className="text-growth-green-500 font-[900] text-sm hover:opacity-80"
                onClick={() => setShowAllTopics(!showAllTopics)}
              >
                {showAllTopics ? 'Show less' : 'View more'}
              </button>
            )}
          </div>

          {/* Mobile-optimized action section */}
          <div className="flex flex-col gap-3 mb-4 md:mb-6">
            {/* Stats and action buttons row */}
            <div className="flex items-center justify-between">
              {/* Left side: Stats */}
              <div className="flex items-center gap-3 md:gap-4">
                <div
                  className="flex items-center gap-1 md:gap-2 cursor-pointer hover:opacity-80"
                  onClick={() => setModal('reaction-modal')}
                >
                  <img className="h-4 md:h-6" src={ReactStackIcon} alt="" />
                  <span className="text-xs md:text-sm font-[600]">
                    {convertCountLike(
                      Object.values(article?.reactions || {})?.reduce(
                        (sum: number, count: number) => sum + count,
                        0
                      )
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <img src={CommentIcon} alt="" className="w-4 h-4 md:w-6 md:h-6" />
                  <span className="text-xs md:text-sm font-[600]">
                    {article?.commentNumber || 0}
                  </span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <img src={EyeDarkIcon} alt="" className="w-4 h-4 md:w-6 md:h-6" />
                  <span className="text-xs md:text-sm font-[600]">{article?.viewNumber || 0}</span>
                </div>
              </div>

              {/* Right side: Share, Bookmark, Menu */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setModal('share-article')}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
                  style={{ minWidth: '36px', minHeight: '36px' }}
                >
                  <img src={ShareIcon} alt="Share" className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                <button
                  onClick={handleSaveArticle}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
                  style={{ minWidth: '36px', minHeight: '36px' }}
                >
                  <img
                    src={article?.isSaved ? SavedIcon : SaveIcon}
                    alt={article?.isSaved ? 'Saved' : 'Save'}
                    className="w-5 h-5 md:w-6 md:h-6"
                  />
                </button>
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="unstyled"
                    border="none"
                    boxShadow="none"
                    h={{ base: '36px', md: '40px' }}
                    w={{ base: '36px', md: '40px' }}
                    rounded="full"
                    _hover={{
                      bg: '#F0F0F0'
                    }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <MoreHorizontal className="w-5 h-5 md:w-6 md:h-6" />
                  </MenuButton>

                  <MenuList minW={'unset'} p={'0.625rem'} shadow={'md'} rounded={'12px'}>
                    <MenuItem
                      _hover={{ bgColor: 'anti-flash-white' }}
                      rounded={'0.5rem'}
                      py={'8px'}
                      px={'10px'}
                      fontSize={'sm'}
                      fontWeight={900}
                      _focus={{ bgColor: 'anti-flash-white' }}
                      icon={<img src={CommentArticleIcon} alt="" className="w-5 h-5" />}
                      onClick={() => handleScrollToGeneralComments()}
                    >
                      Comment on Article
                    </MenuItem>
                    <MenuItem
                      _hover={{ bgColor: 'anti-flash-white' }}
                      rounded={'0.5rem'}
                      py={'8px'}
                      px={'10px'}
                      display={article?.creatorId === profile.id ? 'none' : 'flex'}
                      fontSize={'sm'}
                      fontWeight={900}
                      _focus={{ bgColor: 'anti-flash-white' }}
                      icon={<img src={FlagIcon} alt="" className="w-5 h-5" />}
                      onClick={() => {
                        setOpenReportModal(true);
                        setReportType(ResourceType.ARTICLE);
                      }}
                    >
                      Report Article
                    </MenuItem>
                    <MenuItem
                      _hover={{ bgColor: 'anti-flash-white' }}
                      rounded={'0.5rem'}
                      py={'8px'}
                      px={'10px'}
                      display={article?.creatorId === profile.id ? 'none' : 'flex'}
                      fontSize={'sm'}
                      fontWeight={900}
                      _focus={{ bgColor: 'anti-flash-white' }}
                      icon={
                        <img
                          src={article?.isIgnored ? UnignoreIcon : IgnoreIcon}
                          alt=""
                          className="w-5 h-5"
                        />
                      }
                      onClick={handleIgnoreArticle}
                    >
                      Ignore
                    </MenuItem>
                  </MenuList>
                </Menu>
              </div>
            </div>

            {/* Award and Invest buttons row (full width for active states) */}
            {article?.creator?.id !== profile.id && (
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className={`flex gap-1 items-center py-1 px-2 border ${article?.reputationPoint?.given ? 'border-[#87C98A] bg-growth-green-200' : 'border-[#C8E6C9] bg-06-seasalt-white'} rounded-[80px] w-fit cursor-pointer hover:opacity-80`}
                  onClick={() => setModal('award-writer')}
                >
                  <img
                    src={article?.reputationPoint?.given ? PointBoldIcon : PointIcon}
                    className="w-4 h-4 md:w-6 md:h-6"
                    alt=""
                  />
                  {article?.reputationPoint?.given > 0 ||
                    (article?.reputationPoint?.amount > 0 ? (
                      <></>
                    ) : (
                      <span className="text-xs md:text-sm font-[800] text-growth-green-900">
                        Award
                      </span>
                    ))}
                  <p
                    className={`text-growth-green-900 font-[800] text-xs md:text-sm ${article?.reputationPoint?.amount > 0 ? 'block' : 'hidden'}`}
                  >
                    {article?.reputationPoint?.amount || 0}{' '}
                    {article?.reputationPoint?.amount === 1 ? 'Twig' : 'Twigs'}{' '}
                    <span className="font-[600]">
                      • {article?.reputationPoint?.count || 0}{' '}
                      {article?.reputationPoint?.count === 1 ? 'Reader' : 'Readers'}{' '}
                    </span>
                  </p>
                  {article?.reputationPoint?.given ? (
                    <img src={CheckedIcon} alt="" className="w-4 h-4 md:w-5 md:h-5" />
                  ) : (
                    <img src={PlusGreenIcon} alt="" className="w-4 h-4 md:w-5 md:h-5" />
                  )}
                </div>
                <InvestmentButton article={article} setModal={setModal} />
              </div>
            )}
          </div>

          {article?.cover && (
            <img
              src={article?.cover}
              alt={article?.title}
              className="w-full h-[201px] md:h-[457px] object-cover rounded-lg"
            />
          )}
          {article?.isEnableAISummaries && (
            <div className="rounded-lg w-full mt-2 md:mt-6">
              <Flex
                className={`px-4 md:px-6 py-[10px] items-center justify-between transition-all rounded-2xl bg-[#f2f3f7] shadow-[0px_0px_8px_0px_#00000024]`}
              >
                <Flex className="items-center gap-2">
                  <img src={TwygsLogoIcon} alt="Twygs AI" className="h-6" />
                  <span className="text-growth-green-500 font-[800] text-base md:text-xl">
                    Twygs AI Summary
                  </span>
                </Flex>
                <ExpandButton
                  isExpanded={isExpanded}
                  onClick={() => setIsExpanded((prev) => !prev)}
                />
              </Flex>
              <Collapse in={isExpanded} animateOpacity className="mt-4">
                <Box p={4} className="bg-growth-green-50 rounded-[16px]">
                  <ul className="list-disc ml-4 space-y-2">
                    {article?.aiSummary?.map((point, index) => (
                      <li key={index} className="font-[500] text-lg">
                        {point}
                      </li>
                    ))}
                  </ul>
                </Box>
              </Collapse>
            </div>
          )}
        </div>
      </div>

      {modal === 'award-writer' && (
        <AwardWriterModal
          isOpen
          handleCreatePoint={handleCreatePoint}
          previousPoint={article?.reputationPoint?.given}
          title={`Recognize ${article?.creator?.username || article?.creator?.firstName}'s article`}
          onClose={() => setModal('')}
          isLoading={isCreatingPoint}
          alreadyGivePoint={article?.reputationPoint?.given !== 0}
          dailyLimits={article?.reputationPoint?.dailyLimits}
          contentType="article"
        />
      )}

      {modal === 'invest-article' && (
        <InvestArticleModal
          article={article}
          setOpenLearnMorePanel={setOpenLearnMorePanel}
          handleInvestArticle={handleInvestPoints}
          handleFollowUser={handleFollowUser}
          isFollowingUser={isFollowingUser}
          initialPoints={article?.investmentData?.pointInvested}
          isInvesting={isInvesting}
          alreadyInvested={!!article?.investmentData?.pointInvested}
          isOpen
          onClose={() => setModal('')}
        />
      )}

      {modal === 'share-article' && (
        <ShareContentModal
          isOpen
          title="Share Article"
          smallTitle="Share this Article!"
          shareUrl={`${process.env.REACT_APP_NEWSLETTER_URL}articles/${(article as any)?.alternativeId || article?.id}`}
          media={article?.cover}
          articleId={(article as any)?.alternativeId || article?.id}
          article={article}
          onClick={() => setModal('')}
          titleBtn="Done"
          minW="439px"
        />
      )}
      {modal === 'share-writer' && (
        <ShareContentModal
          isOpen
          userInfo={article?.creator}
          title="Share Writer"
          description="Share this writer profile on social media or copy the link below to send it directly to your audience."
          smallTitle="Share Writer!"
          shareUrl={`${process.env.REACT_APP_NEWSLETTER_URL}${article?.creator?.username}`}
          media={article?.cover}
          onClick={() => setModal('')}
          titleBtn="Done"
          minW="439px"
        />
      )}
      {openReportModal && (
        <ReportSidePanel
          article={article}
          targetType={reportType}
          openReportModal={openReportModal}
          setOpenReportModal={setOpenReportModal}
          handleIgnoreArticle={handleIgnoreArticle}
        />
      )}
      {openLearnMorePanel && <LearnMorePanel isOpen onClose={() => setOpenLearnMorePanel(false)} />}
      {modal === 'reaction-modal' && (
        <ArticleReactionStatsModal
          isOpen
          onClose={() => setModal('')}
          reactions={article.reactions}
          totalComment={article.totalCommentCount}
          articleId={article.id}
        />
      )}
    </div>
  );
};

export default ArticleHeader;
